import * as React from 'react'
import { gql } from '@apollo/client'

import { useCodeValidationLazyQuery, useGetInitialDataLazyQuery } from 'types/graphql'

import { useRouter } from 'next/router'
import { OrganizationContext } from 'v2/context/organization'

import { usePerson } from '../v2/context/person'

export enum LogoutReason {
    General = 'general',
    Inactivity = 'inactivity',
}

interface AuthContextType {
    loading: boolean
}

const InitialValue = {
    loading: true,
}

const AuthContext = React.createContext<AuthContextType>(InitialValue)

interface Props {
    children: React.ReactNode
    loadedTranslations: () => void
}

export const TRANSFER_METHODS_FRAGMENT = gql`
    fragment TransferMethods on PaymentProfileType {
        transferMethods {
            edges {
                node {
                    id
                    type
                    halfLiner
                    oneLiner
                    isCurrentMethod
                    addressLine1
                    addressLine2
                    city
                    zipCode
                    statusCode
                    deleted
                }
            }
        }
    }
`

export const UPDATE_USER = gql`
    mutation UpdateDetails(
        $firstName: String
        $lastName: String
        $phoneNumber: String
        $dob: String
        $addressLine1: String
        $addressLine2: String
        $city: String
        $state: String
        $zipCode: String
    ) {
        updateDetails(
            firstName: $firstName
            lastName: $lastName
            phoneNumber: $phoneNumber
            dob: $dob
            addressLine1: $addressLine1
            addressLine2: $addressLine2
            city: $city
            state: $state
            zipCode: $zipCode
        ) {
            success
        }
    }
`

export const LOGOUT = gql`
    mutation Logout {
        logout {
            ok
        }
    }
`

const GET_ME = gql`
    ${TRANSFER_METHODS_FRAGMENT}
    query Me {
        me {
            id
            email
            createdAt
            pk
            isSuperuser
            communicationSetting {
                phoneNumber
                email
                smsNotificationsEnabled
                emailNotificationsEnabled
                whatsappNotificationsEnabled
            }
            paymentProfile {
                paymentAmount
                paymentFrequencyType
                hyperwalletToken
                numberOfMonths
                frozen
                ...TransferMethods
            }

            person {
                id
                intercomKey
                applicationDetails {
                    program {
                        id
                        urlFragment
                        organization {
                            id
                        }
                    }
                }
                profile {
                    language
                    firstName
                    lastName
                    earnedIncome
                    unearnedIncome
                    dob
                }
                locationDetail {
                    addressLine1
                    addressLine2
                    city
                    state
                    zipCode
                    country
                }
            }
        }
    }
`

const GET_SURVEY_AVAILABLE = gql`
    query SurveyAvailable {
        surveyAvailable
    }
`

const GET_USERS = gql`
    query Users {
        users {
            pk
            communicationSetting {
                phoneNumber
            }
            person {
                profile {
                    firstName
                    language
                    lastName
                    dob
                    earnedIncome
                    unearnedIncome
                }
            }
            paymentProfile {
                paymentAmount
                paymentFrequencyType
                startDate
                frozen
            }
        }
    }
`

const AuthContextProvider: React.FC<Props> = (props: Props) => {
    const router = useRouter()
    const organizationContext = React.useContext(OrganizationContext)
    const { children } = props
    const personContext = usePerson()

    const [
        fetchPerson,
        { data: dataPerson, loading: codeValidationLoading, called: calledPerson },
    ] = useCodeValidationLazyQuery()

    const fetchPersonIfNecessary = () => {
        const urlSearchParams = new URLSearchParams(window.location.search)
        if (window.location.pathname.includes('sign-up') && urlSearchParams.get('ic')) {
            fetchPerson({ variables: { inviteCode: urlSearchParams.get('ic') } })
        }
        if (window.location.pathname.includes('reset-password')) {
            fetchPerson({
                variables: {
                    resetToken: window.location.pathname.split('/')[window.location.pathname.split('/').length - 1],
                },
            })
        }
    }

    const [getInitialData, initialData] = useGetInitialDataLazyQuery()

    React.useEffect(() => {
        fetchPersonIfNecessary()
        if (!codeValidationLoading) {
            getInitialData()
        }
    }, [])

    React.useEffect(() => {
        if (calledPerson) {
            if (dataPerson) {
                personContext.setPerson(dataPerson?.codeValidation?.person)
                personContext.setAlreadySignedUp(dataPerson?.codeValidation?.alreadySignedUp)
                window.PERSON = dataPerson?.codeValidation?.person
            }
        }

        if (!codeValidationLoading && !initialData.called) {
            getInitialData()
        }
    }, [dataPerson])

    const organizationConfigs = React.useMemo(() => {
        return initialData?.data?.organizationConfigs
    }, [initialData?.data])

    const organization = React.useMemo(() => {
        return initialData?.data?.organization
    }, [initialData?.data])

    const program = React.useMemo(() => {
        if (
            (initialData?.data || initialData?.error) &&
            !initialData?.data?.program?.urlFragment &&
            !window.location.href.includes('reset-password')
        ) {
            router.push('/404')
        } else {
            return initialData?.data?.program
        }
    }, [initialData?.data, initialData?.error])

    React.useEffect(() => {
        if (initialData?.data) {
            const languages = initialData?.data.languages

            const jsonBody = initialData?.data?.translations
            if (!jsonBody) {
                return
            }

            const jsonBodyForTranslations = JSON.parse(jsonBody)[0]
            languages.forEach((lang) => {
                const namespaces = Object.keys(jsonBodyForTranslations[lang])
                namespaces.forEach((ns) => {
                    //TODO: This is bad. We'll need to handle these based on some kind of order enum
                    window.LANGUAGES = JSON.parse(jsonBody)[1]
                        .filter((itm) => languages.includes(itm.code))
                        .map((language) => {
                            if (language.code === 'fa') {
                                return {
                                    code: 'fa',
                                    text: 'Farsi',
                                }
                            } else if (language.code === 'vi') {
                                return {
                                    code: 'vi',
                                    text: 'Tiếng Việt',
                                }
                            } else if (language.code === 'es') {
                                return {
                                    code: 'es',
                                    text: 'Español',
                                }
                            } else {
                                return language
                            }
                        })

                    window.S18N.addResources(lang, ns, jsonBodyForTranslations[lang][ns])
                })
            })
            props.loadedTranslations()
        }

        if (initialData?.error) {
            props.loadedTranslations()
        }
    }, [initialData?.data, initialData?.error])

    React.useMemo(() => {
        if (initialData?.data?.documentRequirement) {
            window.DOCUMENT_REQ = initialData?.data?.documentRequirement
        }
    }, [initialData?.data])

    if (typeof window !== 'undefined') {
        organizationContext.setOrganization(organization)
        organizationContext.setOrganizationConfigs(organizationConfigs)
        organizationContext.setProgram(program)

        window.ORGANIZATION_CONFIGS = {
            phoneNumber: organizationConfigs?.phoneNumber,
            timezone: organizationConfigs?.timezone,
            replyToEmail: organizationConfigs?.replyToEmail,
            typeformId: organizationConfigs?.typeformId,
            websiteLink: organizationConfigs?.websiteLink,
            instagramLink: organizationConfigs?.instagramLink,
            facebookLink: organizationConfigs?.facebookLink,
            twitterLink: organizationConfigs?.twitterLink,
            typeformSubdomain: organizationConfigs?.typeformSubdomain,
            isTypeformBound: organizationConfigs?.isTypeformBound,
            whatsappPhoneNumber: organizationConfigs?.whatsappPhoneNumber,
        }

        window.ORGANIZATION = {
            version: organization?.version,
            name: organization?.name,
            id: organization?.id,
            webAppUrl: organization?.webAppUrl,
        }

        window.PROGRAM = {
            name: program?.name || '',
            paymentFrequency: program?.paymentFrequency || {},
            paymentAmount: program?.paymentAmount,
            numberOfMonths: program?.numberOfMonths,
            ssiBenefitsWizardEnabled: program?.ssiBenefitsWizardEnabled,
            id: program?.id,
            programDuration: JSON.parse(program?.programDuration || '{}'),
            urlFragment: program?.urlFragment || '',
        }
    }

    return (
        <AuthContext.Provider
            value={{
                loading: codeValidationLoading,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export { AuthContext, AuthContextProvider }
